<template>
  <div class="moderation-playground bg-gray-50 dark:bg-gray-900 p-6">
    <div class="max-w-3xl mx-auto">
      <Card>
        <h3
          class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          Content Moderation
        </h3>

        <!-- No Models Warning -->
        <Alert v-if="!isLoadingModels && models.length === 0" variant="warning" class="mb-4">
          No moderation models available. Please check your API configuration.
        </Alert>

        <!-- Model Picker -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Model</label
          >
          <Select
            :model-value="selectedModel"
            :options="modelOptions"
            :disabled="isLoadingModels || models.length === 0"
            placeholder="Select moderation model"
            @update:model-value="handleModelChange"
          />
        </div>

        <!-- Text Input -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Content to Moderate</label
          >
          <textarea
            v-model="inputText"
            rows="4"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter content to check for policy violations..."
          />
        </div>

        <!-- Moderate Button -->
        <Button
          variant="primary"
          :disabled="!canModerate || isLoading"
          :loading="isLoading"
          @click="handleModerate"
          class="w-full"
        >
          Check Content
        </Button>

        <!-- Results -->
        <div v-if="results" class="mt-6 space-y-4">
          <!-- Overall Status -->
          <div
            :class="[
              'p-4 rounded-lg border',
              results.flagged
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
            ]"
          >
            <div class="flex items-center gap-2">
              <svg
                v-if="results.flagged"
                class="w-5 h-5 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <svg
                v-else
                class="w-5 h-5 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span
                :class="
                  results.flagged
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-green-700 dark:text-green-300'
                "
                class="font-medium"
              >
                {{ results.flagged ? 'Content Flagged' : 'Content Safe' }}
              </span>
            </div>
          </div>

          <!-- Category Scores -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Category Scores</label
            >
            <div class="space-y-2">
              <div
                v-for="(score, category) in results.categoryScores"
                :key="category"
                class="flex items-center gap-3"
              >
                <span class="text-sm text-gray-600 dark:text-gray-400 w-32 truncate">{{
                  formatCategory(category)
                }}</span>
                <div class="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    :class="[
                      'h-full rounded-full transition-all',
                      score > 0.5 ? 'bg-red-500' : score > 0.2 ? 'bg-yellow-500' : 'bg-green-500',
                    ]"
                    :style="{ width: `${Math.min(score * 100, 100)}%` }"
                  />
                </div>
                <span class="text-xs text-gray-500 dark:text-gray-400 w-12 text-right">
                  {{ (score * 100).toFixed(1) }}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { Alert, Button, Select, Card } from '@asabdev/asab-ui';
  import { useModerationPlayground } from '../../composables/useModerationPlayground';
  import type { Model } from '../../types/models';

  interface Props {
    models: Model[];
    isLoadingModels: boolean;
  }

  const props = defineProps<Props>();

  const { inputText, selectedModel, results, isLoading, moderateContent, setModel } =
    useModerationPlayground();

  const modelOptions = computed(() => props.models.map(m => ({ value: m.id, label: m.name })));

  const canModerate = computed(() => inputText.value.trim() && selectedModel.value);

  onMounted(() => {
    if (props.models.length > 0 && !selectedModel.value) {
      setModel(props.models[0].id);
    }
  });

  const handleModelChange = (value: string) => setModel(value);

  const handleModerate = async () => {
    await moderateContent(inputText.value);
  };

  const formatCategory = (category: string): string => {
    return category
      .replace(/_/g, ' ')
      .replace(/\//g, ' / ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
</script>
